/**
 * Navigation primitives: the tab strip (buttons or router links) and the
 * popover menu anchored to its trigger. Re-exported through `./primitives.js`.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Pix } from './feedback.js';
import { useEscape } from './overlays.js';

/* ---- Tabs ------------------------------------------------------------ */

export interface TabItem {
  readonly key: string;
  readonly label: ReactNode;
  readonly count?: number | undefined;
  readonly alert?: boolean | undefined;
  readonly href?: string | undefined;
}

export function Tabs({
  tabs,
  active,
  label,
  onSelect,
}: {
  readonly tabs: readonly TabItem[];
  readonly active: string;
  readonly label: string;
  readonly onSelect?: (key: string) => void;
}): ReactNode {
  return (
    <div className="mj-ws-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => {
        const content = (
          <>
            {tab.label}
            {tab.count !== undefined && tab.count > 0 ? (
              <span className="mj-ws-badge mj-ws-tab-count">{tab.count}</span>
            ) : null}
            {tab.alert ? <Pix status="needs-input" /> : null}
          </>
        );
        const selected = tab.key === active;
        return tab.href !== undefined ? (
          <Link
            key={tab.key}
            role="tab"
            className="mj-ws-tab"
            aria-selected={selected}
            to={tab.href}
          >
            {content}
          </Link>
        ) : (
          <button
            key={tab.key}
            type="button"
            role="tab"
            className="mj-ws-tab"
            aria-selected={selected}
            onClick={() => onSelect?.(tab.key)}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

export interface MenuItem {
  readonly key: string;
  readonly label: ReactNode;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly checked?: boolean;
  readonly icon?: ReactNode;
  readonly trailing?: ReactNode;
}

/** A popover menu anchored to its trigger; closes on outside click and Escape. */
export function Menu({
  open,
  onClose,
  items,
  label,
  align = 'start',
  header,
  footer,
  className = '',
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly items: readonly MenuItem[];
  readonly label: string;
  readonly align?: 'start' | 'end';
  readonly header?: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
}): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  useEscape(open, onClose);
  useEffect(() => {
    if (!open) return undefined;
    const listener = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && ref.current?.parentElement?.contains(target))
        return;
      onClose();
    };
    document.addEventListener('mousedown', listener);
    const timer = window.setTimeout(() => {
      ref.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus();
    }, 30);
    return () => {
      document.removeEventListener('mousedown', listener);
      window.clearTimeout(timer);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      className={`mj-ws-menu is-${align} ${className}`.trim()}
      role="menu"
      aria-label={label}
    >
      {header}
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
          aria-checked={item.checked}
          className="mj-ws-mitem"
          disabled={item.disabled}
          onClick={() => {
            item.onSelect();
          }}
        >
          {item.icon}
          <span className="mj-ws-mitem-label">{item.label}</span>
          {item.trailing}
        </button>
      ))}
      {footer}
    </div>
  );
}
